import {
  OFFICE_EXCLUSIVE_FILENAME,
  OFFICE_EXCLUSIVE_SHORT_DESC,
  OfficeExclusiveType,
} from '../../../../../../src/components/edit/control/OfficeExclusiveDocument';
import { isMimeTypeNullError } from '../../../../../../src/system/media';
import { MediaMetadata, UploadStage } from './types';

/**
 * Returns the Base64 data string of a given file.
 */
export const getBase64Data = async (file: File) => {
  return new Promise<string>((resolve) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      resolve(result.split(',')[1]);
    };
    reader.readAsDataURL(file);
  });
};

/**
 * Get status text for a given stage
 */
export const getStageStatusText = (stage: UploadStage): string => {
  switch (stage) {
    case 'preparing':
      return 'Preparing upload...';
    case 'reading':
      return 'Reading file...';
    case 'uploading':
      return 'Creating media record...';
    case 'sending':
      return 'Uploading content...';
    case 'processing':
      return 'Processing image...';
    case 'complete':
      return 'Upload complete!';
    case 'error':
      return 'Upload failed';
    default:
      return 'Uploading...';
  }
};

/**
 * Helper function to validate URLs
 */
export const isValidUrl = (url: any): boolean => {
  return (
    typeof url === 'string' &&
    (url.startsWith('http://') || url.startsWith('https://'))
  );
};

/**
 * Validates if a media resource has all required properties and valid URLs
 */
export const isMediaResourceValid = (resource: any): boolean => {
  // Basic validation
  if (!resource || !resource.MED_VW_KEY || !resource.MEDIA_CATEGORY) {
    console.warn('Invalid media resource: missing basic properties', resource);
    return false;
  }

  // For photos, validate URLs
  if (resource.MEDIA_CATEGORY === 'Photo') {
    // Check main URL
    const hasValidUrl = isValidUrl(resource.MEDIA_URL);

    // Check thumbnail URL
    const hasValidThumb = isValidUrl(resource.MEDIA_URL_THUMB);

    // Check medium URL (optional for backward compatibility)
    const hasValidMedium =
      !resource.MEDIA_URL_MEDIUM || isValidUrl(resource.MEDIA_URL_MEDIUM);

    if (!hasValidUrl || !hasValidThumb || !hasValidMedium) {
      return false;
    }

    return true;
  }

  // For other media types, basic validation is sufficient
  return true;
};

/**
 * Retry a function with exponential backoff
 * @param fn - The async function to retry
 * @param maxRetries - Maximum number of retry attempts (default: 3)
 * @param initialDelay - Initial delay in milliseconds (default: 1000ms)
 * @param backoffFactor - Exponential backoff factor (default: 2)
 * @param logger
 * @returns The result of the function
 */
export async function retryWithExponentialBackoff<T>(
  fn: () => Promise<T>,
  maxRetries: number = 3,
  initialDelay: number = 1000,
  backoffFactor: number = 2,

  logger?: {
    log?: (message: string) => void;
    error?: (message: string, error?: any) => void;
  },
): Promise<T> {
  const log = logger?.log || console.log;
  const error = logger?.error || console.error;

  let lastError: Error;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      log(`Attempt ${attempt + 1} of ${maxRetries + 1}`);
      return await fn();
    } catch (err) {
      lastError = err instanceof Error ? err : new Error(String(err));

      // Don't retry MimeType errors - they're data issues, not transient failures
      if (isMimeTypeNullError(lastError)) {
        error('MimeType error detected - not retrying', lastError);
        throw lastError;
      }

      if (attempt === maxRetries) {
        error(`All ${maxRetries + 1} attempts failed`, lastError);
        throw lastError;
      }

      const delay = initialDelay * Math.pow(backoffFactor, attempt);
      log(
        `Attempt ${attempt + 1} failed, retrying in ${delay}ms...`,
        lastError.message,
      );

      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  }

  throw lastError!;
}

/**
 * Get Media source file Name
 * For OE docs the name will be OFFICE_EXCLUSIVE_FILENAME
 */
export const getMediaSourceFileName = (metadata: MediaMetadata) => {
  const isOE = metadata.docType === OfficeExclusiveType.OFFICE_EXCLUSIVE;

  if (isOE) {
    return OFFICE_EXCLUSIVE_FILENAME;
  }

  return metadata.file?.name || null;
};

/**
 * Get Media Short Description
 * For OE docs the desc will be OFFICE_EXCLUSIVE_SHORT_DESC
 */
export const getMediaShortDescription = (metadata: MediaMetadata) => {
  const isOE = metadata.docType === OfficeExclusiveType.OFFICE_EXCLUSIVE;

  if (isOE) {
    // returning Office Exclusive Document
    return OFFICE_EXCLUSIVE_SHORT_DESC;
  }

  return metadata.shortDescription || '';
};
